import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Paperclip, X, Loader2, Image as ImageIcon, FileText, File as FileIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fileIconFor = (contentType) => {
  if ((contentType || '').startsWith('image/')) return ImageIcon;
  if ((contentType || '').includes('pdf') || (contentType || '').includes('document')) return FileText;
  return FileIcon;
};

// Shared upload widget for ticket attachments: click-to-browse, drag/drop, and
// paste-a-screenshot (Ctrl/Cmd+V while focused on the textarea this wraps).
// Files are uploaded immediately to the given endpoint (per-customer iDrive
// folder, handled server-side) and the resulting attachment metadata is
// tracked in `attachments` for the parent to submit with the ticket/reply.
export function AttachmentUploader({ uploadEndpoint, token, attachments, setAttachments, testIdPrefix }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      alert('Attachment must be 15MB or smaller');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${BACKEND_URL}${uploadEndpoint}`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setAttachments((prev) => [...prev, res.data]);
    } catch (error) {
      alert(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        e.preventDefault();
        const file = items[i].getAsFile();
        if (file) uploadFile(file);
        return;
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadFile(file);
  };

  const removeAttachment = (key) => {
    setAttachments((prev) => prev.filter((a) => a.key !== key));
  };

  return (
    <div onPaste={handlePaste} onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}>
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/*,.pdf,.doc,.docx,.txt"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ''; }}
        data-testid={`${testIdPrefix}-file-input`}
      />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-[#6e2ea8] transition-colors"
        data-testid={`${testIdPrefix}-attach-button`}
      >
        {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
        Attach file or paste a screenshot
      </button>

      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2" data-testid={`${testIdPrefix}-attachment-list`}>
          {attachments.map((att) => {
            const Icon = fileIconFor(att.content_type);
            return (
              <div key={att.key} className="flex items-center gap-1.5 bg-slate-100 rounded-lg px-2 py-1 text-xs text-slate-600">
                <Icon className="w-3.5 h-3.5" />
                <span className="max-w-[120px] truncate">{att.filename}</span>
                <button type="button" onClick={() => removeAttachment(att.key)} data-testid={`${testIdPrefix}-remove-attachment`}>
                  <X className="w-3.5 h-3.5 hover:text-red-500" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export const renderAttachmentPreview = (att, idx) => {
  const Icon = fileIconFor(att.content_type);
  const isImage = (att.content_type || '').startsWith('image/');
  return isImage ? (
    <a key={idx} href={`${BACKEND_URL}${att.url}`} target="_blank" rel="noopener noreferrer">
      <img src={`${BACKEND_URL}${att.url}`} alt={att.filename} className="max-w-full rounded-lg max-h-52 object-cover mb-2" />
    </a>
  ) : (
    <a
      key={idx}
      href={`${BACKEND_URL}${att.url}`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 p-2 rounded-lg bg-white/60 mb-2 text-sm"
    >
      <Icon className="w-4 h-4" />
      <span className="truncate">{att.filename}</span>
    </a>
  );
};
